import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Loader2, X } from 'lucide-react';

interface TossPaymentsCheckoutProps {
  clientKey: string;
  amount: number;
  orderId: string;
  orderName: string;
  successUrl: string;
  failUrl: string;
  onClose: () => void;
}

declare global {
  interface Window {
    TossPayments: any;
  }
}

/**
 * 토스페이먼츠 결제창(SDK) 방식 컴포넌트
 *
 * 특징:
 * - API 개별 연동 키 사용
 * - 약관을 직접 구현 (미체크 상태로 시작)
 * - Admin 패널 접근 불필요
 * - 팝업 결제창 방식
 */
export const TossPaymentsCheckout: React.FC<TossPaymentsCheckoutProps> = ({
  clientKey,
  amount,
  orderId,
  orderName,
  successUrl,
  failUrl,
  onClose,
}) => {
  const [sdkLoading, setSdkLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // 약관 동의 상태 (직접 관리)
  const [agreements, setAgreements] = useState({
    terms: false,        // 이용약관 동의 (필수)
    privacy: false,      // 개인정보 수집 및 이용 동의 (필수)
    payment: false,      // 결제대행 서비스 약관 동의 (필수)
  });

  // SDK 로드
  useEffect(() => {
    const loadScript = () => {
      if (window.TossPayments) {
        setSdkLoading(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.tosspayments.com/v1/payment';
      script.async = true;
      script.onload = () => {
        console.log('토스페이먼츠 SDK 로드 완료');
        setSdkLoading(false);
      };
      script.onerror = () => {
        setError('결제 시스템을 불러오는데 실패했습니다. 페이지를 새로고침해주세요.');
        setSdkLoading(false);
      };
      document.body.appendChild(script);
    };

    loadScript();
  }, []);

  // 모든 필수 약관 동의 여부 확인
  const allAgreed = agreements.terms && agreements.privacy && agreements.payment;

  // 약관 체크박스 변경
  const handleAgreementChange = (key: keyof typeof agreements) => {
    setAgreements(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 전체 동의
  const handleAllAgree = () => {
    const newValue = !allAgreed;
    setAgreements({
      terms: newValue,
      privacy: newValue,
      payment: newValue,
    });
  };

  // 결제 요청
  const handlePayment = async () => {
    if (!allAgreed) {
      setError('필수 약관에 모두 동의해주세요.');
      return;
    }

    if (!window.TossPayments) {
      setError('결제 시스템이 초기화되지 않았습니다.');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      const tossPayments = window.TossPayments(clientKey);

      // 결제창 호출
      await tossPayments.requestPayment('카드', {
        amount,
        orderId,
        orderName,
        successUrl,
        failUrl,
        customerName: undefined,
        customerEmail: undefined,
      });
    } catch (err: any) {
      console.error('결제 요청 실패:', err);
      if (err.code === 'USER_CANCEL') {
        setError('결제가 취소되었습니다.');
      } else {
        setError(err.message || '결제 요청 중 오류가 발생했습니다.');
      }
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">결제하기</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={processing}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {error && (
            <div className="mb-4">
              <AlertMessage
                type="error"
                message={error}
                onClose={() => setError('')}
              />
            </div>
          )}

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">주문 금액</span>
              <span className="text-2xl font-bold text-blue-600">
                {Math.floor(amount).toLocaleString()}원
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-gray-600">주문명</span>
              <span className="text-sm font-medium">{orderName}</span>
            </div>
          </div>

          {sdkLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
              <p className="text-gray-600">결제 시스템을 불러오는 중...</p>
            </div>
          ) : (
            <>
              {/* 약관 동의 섹션 */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold mb-4">약관 동의</h3>

                {/* 전체 동의 */}
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allAgreed}
                      onChange={handleAllAgree}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="font-medium text-gray-900">
                      전체 동의
                    </span>
                  </label>
                </div>

                {/* 개별 약관 */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={agreements.terms}
                      onChange={() => handleAgreementChange('terms')}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      id="terms"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-700 flex-1 cursor-pointer">
                      [필수] 전자금융거래 이용약관 동의
                      <a
                        href="https://pages.tosspayments.com/terms/user/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        보기
                      </a>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={agreements.privacy}
                      onChange={() => handleAgreementChange('privacy')}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      id="privacy"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-700 flex-1 cursor-pointer">
                      [필수] 개인정보 처리방침 동의
                      <a
                        href="https://pages.tosspayments.com/terms/homepage/privacy/policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        보기
                      </a>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={agreements.payment}
                      onChange={() => handleAgreementChange('payment')}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      id="payment"
                    />
                    <label htmlFor="payment" className="text-sm text-gray-700 flex-1 cursor-pointer">
                      [필수] 전자결제서비스 이용계약 동의
                      <a
                        href="https://pages.tosspayments.com/terms/onboarding/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        보기
                      </a>
                    </label>
                  </div>
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  ※ 전자상거래법 및 개인정보보호법에 따라 필수 약관에 동의해야 결제가 가능합니다.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={processing}
                  className="flex-1"
                >
                  취소
                </Button>
                <Button
                  onClick={handlePayment}
                  disabled={processing || !allAgreed}
                  className="flex-1"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    '결제하기'
                  )}
                </Button>
              </div>

              {!allAgreed && (
                <p className="mt-3 text-sm text-center text-red-500">
                  필수 약관에 모두 동의해주세요
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
